import { useMemo } from 'react';
import {
  Paper,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  AccessRequestClientModel,
  AccessStatus,
  PaginatedClientResponseModelAccessRequestClientModel,
} from '@apono/client-app-sdk';

import { TableColWrapper, TableWrapper } from '@common/ui/TableWrapper';
import { Status } from '@common/ui/Status';
import { PagedPagination } from '@common/ui/PagedPagination';
import { TableLoading } from '@common/ui/TableLoading';
import IntegrationsCell from '@common/components/IntegrationsCell';

import RequestAgain from './RequestAgain';
import ResourceTypesCell from '@common/components/ResourceTypesCell';
import ChallengeCell from '@common/components/ChalengesCell';
import { CustomMouseEvent, isEventHandledByButton, markEventHandledByButton } from '@common/utils/mouse-events';
import { AnalyticsEvents } from '@common/utils/analytics/events';
import PrimarySecondary from '@common/ui/PrimarySecondary';
import { DateTime } from '@common/utils/datetime';

export default function ClosedRequestsTable({
  isFetching,
  pageData: { data, pagination },
  selectedRequestId,
  handleRequestClick,
  handleOnPageChange,
}: {
  isFetching: boolean;
  pageData: PaginatedClientResponseModelAccessRequestClientModel;
  selectedRequestId?: string;
  handleRequestClick: (requestId: string) => void;
  handleOnPageChange: (page: number) => void;
}) {
  return useMemo(
    () => (
      <Stack direction="column" justifyContent="center" alignItems="stretch" spacing={2}>
        <TableWrapper>
          <TableLoading loading={isFetching} />
          <TableContainer component={Paper} elevation={0}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell component="th" size="medium">
                    <TableColWrapper>ID</TableColWrapper>
                  </TableCell>
                  <TableCell component="th" size="medium">
                    <TableColWrapper>Integrations</TableColWrapper>
                  </TableCell>
                  <TableCell component="th" size="medium">
                    <TableColWrapper>Resource Types</TableColWrapper>
                  </TableCell>
                  <TableCell component="th" size="medium">
                    <TableColWrapper>Approvals</TableColWrapper>
                  </TableCell>
                  <TableCell component="th" size="medium">
                    <TableColWrapper>Status</TableColWrapper>
                  </TableCell>
                  <TableCell component="th" size="medium" width="300"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((request) => {
                  return (
                    <TableRow
                      data-trigger={AnalyticsEvents.ClosedRequestClicked}
                      data-props={JSON.stringify({ status: request.status.status })}
                      key={request.id}
                      hover
                      className="MuiTableRow-clickable"
                      onClick={(e: CustomMouseEvent) => {
                        if (isEventHandledByButton(e)) return;
                        handleRequestClick(request.id);
                      }}
                      selected={selectedRequestId === request.id}
                    >
                      <TableCell component="td">
                        <PrimarySecondary
                          primary={request.id}
                          secondary={DateTime.fromUnix(Number(request.creation_time)).readable}
                        />
                      </TableCell>
                      <TableCell component="td">
                        <IntegrationsCell
                          integrations={request.access_groups.map((ag) => ag.integration).flat()}
                          bundleName={request.bundle?.name}
                        />
                      </TableCell>
                      <TableCell component="td">
                        <ResourceTypesCell
                          resourceTypes={request.access_groups.map((ag) => ag.resource_types).flat()}
                        />
                      </TableCell>
                      <TableCell component="td">
                        <ChallengeCell challenge={request.challenge} />
                      </TableCell>
                      <TableCell component="td">
                        <StatusColumn request={request} />
                      </TableCell>
                      <TableCell component="td" align="right" onClick={markEventHandledByButton}>
                        <RequestAgain request={request} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </MuiTable>
          </TableContainer>
        </TableWrapper>
        <PagedPagination isLoading={isFetching} pagination={pagination} onChange={handleOnPageChange} />
      </Stack>
    ),
    [isFetching, data, pagination, selectedRequestId, handleRequestClick, handleOnPageChange],
  );
}

function StatusColumn({ request }: { request: AccessRequestClientModel }) {
  const isError = request.status.status === AccessStatus.Rejected;
  const approverReason = request.challenge?.approvers.find((challenge) => challenge.status === AccessStatus.Rejected)
    ?.metadata.approver_reason; // let question the reality

  const StatusEl = <Status status={request.status.status} description={request.status.description} />;

  if (!isError || !approverReason) {
    return StatusEl;
  }

  return (
    <Tooltip color="error" title={<RejectedTooltipContent text={approverReason} />} placement="left">
      <div>{StatusEl}</div>
    </Tooltip>
  );
}

function RejectedTooltipContent({ text }: { text: string }) {
  return (
    <Stack direction="column">
      <Typography variant="helperText" color="error">
        Rejection Reason:
      </Typography>
      <Typography variant="helperText">{text}</Typography>
    </Stack>
  );
}
